import { createStore } from "zustand";
import { CellAddress, DataTableStoreState } from "./DataTableProvider.types";
import { immer } from "zustand/middleware/immer";
import { Project, Tenant } from "graphql/_Types";
import * as DjangoClient from "./DataTableProvider.api";
import { chain, clone, uniqWith } from "lodash";
import {
  IdeasFunctionPluginTranslations,
  initSpreadsheetEngine,
} from "./engine";
import assert from "assert";
import { isDefined } from "utils/isDefined";
import { createProjectActionQueue } from "./queue";
import {
  Ast,
  AstNodeType,
  CellStatuses,
  CellValue,
  DetailedCellError,
  ExportedCellChange,
  Group,
  HyperFormula,
  IdeasFile,
  IdeasFileNotFound,
  RoiFrame,
  RowIdentifier,
  Sheets,
  SimpleCellAddress,
} from "@inscopix/ideas-hyperformula";
import {
  AnalysisParamValue,
  convertCellValueToAnalysisParamValues,
  recalculateCellValues,
  SideEffects,
  traverseAst,
} from "./SideEffects";
import { QueuedFile } from "stores/upload/FileUploadProvider.helpers";
import { ProcessingStatus, TaskStatus } from "types/constants";
import { FILE_TYPES_BY_KEY } from "types/FileTypes";
import { identifyFile } from "utils/identifyFile";
import { isTerminalTaskStatus } from "utils/isTerminalTaskStatus";
import { downloadFile } from "utils/downloadFile";
import { captureException } from "@sentry/react";
import { NavigateOptions } from "react-router-dom-v5-compat";

/**
 * Formats a cell value for CSV export.
 * @param value The cell value.
 * @returns The formatted cell value.
 */
export const formatCellValue = (value: CellValue): string => {
  if (
    typeof value === "boolean" ||
    typeof value === "number" ||
    typeof value === "string"
  ) {
    return value.toString();
  }

  if (value instanceof IdeasFile) {
    return value.attrs.name;
  }

  if (value instanceof Group) {
    return value.cellValues.map(formatCellValue).join(",");
  }

  if (value instanceof RowIdentifier) {
    return `${value.tableKey}-${value.rowIndex}`;
  }

  if (value instanceof RoiFrame) {
    return JSON.stringify(value.shapes);
  }

  if (value instanceof CellStatuses) {
    return value.statuses.join(",");
  }

  if (
    value instanceof DetailedCellError ||
    value === null ||
    value === IdeasFileNotFound
  ) {
    return "";
  }

  // Since we cannot use a switch statement here, we check that the
  // type of value is never. This ensures that when the CellValue type
  // changes, compiler errors are thrown and this function is updated.

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const exhaustiveCheck: never = value;
  captureException("Exhaustive check failed in formatCellValue");
  return "";
};

type CreateDataTableOptions = {
  tenantId: Tenant["id"];
  projectKey: Project["key"];
  initialTableKey: string | undefined;
  navigateToTable: (
    tableKey: string | undefined,
    options?: NavigateOptions,
  ) => void;
  enqueueFile: (file: QueuedFile) => void;
  cancelFileUploads: (fileIds: string[]) => void;
};

/**
 * Create a new Zustand store for storing data table and analysis table data
 * and actions for updating the stored data.
 * @param projectKey
 * @returns The Zustand store.
 */
export const createDataTableStore = async ({
  tenantId,
  projectKey,
  initialTableKey,
  navigateToTable,
  enqueueFile,
  cancelFileUploads,
}: CreateDataTableOptions) => {
  // Fetch cell formulas from server
  const project = await DjangoClient.getProject({ projectKey });
  const data = await DjangoClient.getTableData({ projectId: project.id });

  // Initialize data and define actions
  return createStore<DataTableStoreState>()(
    immer((set, get) => {
      // Initialize a new empty HyperFormula engine instance
      let engine = HyperFormula.buildEmpty();

      // Wait until the store is initialized to register the sheets with the engine.
      // This is necessary because the formula context, uses `get` which will not
      // return data until after the store has been created.
      setTimeout(function registerSheets() {
        const isStoreInitialized = get() !== undefined;

        if (!isStoreInitialized) {
          setTimeout(registerSheets, 1);
          return;
        }

        // Format server data into HyperFormula sheets
        const sheets: Sheets = chain(data)
          .keyBy((table) => table.key)
          .mapValues((table) =>
            table.rows.map((row) =>
              row.cells.map((cell) => cell.formula ?? ""),
            ),
          )
          .value();

        engine = initSpreadsheetEngine(sheets, get);

        // Set the initial cell values in the store
        set((state) => {
          state.tables.forEach((table) => {
            const sheetId = engine.getSheetId(table.key);
            assert(isDefined(sheetId));
            table.rows.forEach((row, rowIdx) => {
              row.cells.forEach((cell, columnIdx) => {
                cell.value = engine.getCellValue({
                  sheet: sheetId,
                  col: columnIdx,
                  row: rowIdx,
                });
              });
            });
          });
        });

        // Initialize callback to update store state when cell values change
        engine.on("valuesUpdated", (changes) => {
          set((state) => {
            changes.forEach((change) => {
              if (change instanceof ExportedCellChange) {
                // Update cell value in store state
                const { address, newValue } = change;
                const tableKey = engine.getSheetName(address.sheet);
                const table = state.tables.find(({ key }) => key === tableKey);
                assert(isDefined(table));
                const cell = table.rows[address.row].cells[address.col];
                cell.value = newValue;
              }
            });
          });
        });
      }, 1);

      /**
       * Converts an IDEAS cell address into a HyperFormula cell address.
       * @param address An IDEAS cell address.
       * @returns The address as a HyperFormula cell address.
       */
      const convertIdeasCellAddress = (address: CellAddress) => {
        const { tableId, columnId, rowId } = address;
        const table = get().tables.find(({ id }) => id === tableId);
        assert(isDefined(table), "Failed to read table ID from cell address");
        const sheetId = engine.getSheetId(table.key);
        assert(isDefined(sheetId), "Failed to read sheet ID from cell address");
        const colIdx = table.columns.findIndex(({ id }) => id === columnId);
        const rowIdx = table.rows.findIndex(({ id }) => id === rowId);
        return { sheet: sheetId, col: colIdx, row: rowIdx };
      };

      // Format store data using the cell values computed by the engine
      const tables = data.map((table) => ({
        ...table,
        toolVersion: table.tool_version,
        columnGroups: table.column_groups,
        columns: table.columns.map((column, idx) => ({
          ...column,
          // Compensate for gaps in column order in the database
          order: idx,
        })),
        rows: table.rows.map((row) => ({
          id: row.id,
          index: row.index,
          editable: row.editable,
          joinable: row.joinable,
          task: row.task ?? null,
          toolVersion: row.tool_version ?? null,
          resourceTierId: row.resource_tier ?? null,
          cells: row.cells.map((cell) => ({
            isCustomFormula: cell.is_custom_formula,
            formula: cell.formula ?? "",
            value: null,
          })),
        })),
      }));

      // Initialize a new action queue
      const { enqueueAction } = createProjectActionQueue(set, get);

      return {
        project,

        tables,

        flyoutRightContent: "fileInfo" as const,

        flyoutBottomContent: undefined,

        gridApi: undefined,

        selectedTableId: tables.find(({ key }) => key === initialTableKey)?.id,

        selectedFileId: undefined,

        selectedRowIds: [],

        isAdvancedMode: false,

        syncStatus: {
          status: "idle",
        },

        fileMap: {
          filesById: {},
          filesByReferenceId: {},
        },

        metadatumMap: {},

        /**
         * Gets a file from the cache. If the file has not been previously
         * fetched, retrieve it from the server and notify the caller through a
         * callback.
         * @param options
         * @returns The cached file.
         */
        getFile: ({ fileReferenceId, address, engine }) => {
          const file = get().fileMap.filesByReferenceId[fileReferenceId];

          if (file === undefined) {
            void DjangoClient.getProjectFiles({
              fileReferenceId,
              projectId: project.id,
              setStoreState: set,
              engine,
              address,
            })
              .then(() => undefined)
              .catch(() => undefined);
          }

          return file;
        },

        /**
         * Gets a file metadatum value from the cache. If the value has not
         * been previously fetched, retrieve it from the server and notify
         * the caller through a callback.
         * @param options
         * @returns The cached metadatum value.
         */
        getFileMetadatum: ({ fileId, metadatumKey, address, engine }) => {
          const metadatumValue = get().metadatumMap[fileId]?.[metadatumKey];

          if (metadatumValue === undefined) {
            DjangoClient.getFileMetadatumValue({
              fileId,
              metadatumKey,
              setStoreState: set,
              address,
              engine,
            })
              .then(() => undefined)
              .catch(() => undefined);
          }

          return metadatumValue;
        },

        /**
         * Sets the content of the right flyout. Defaults to the file info if nothing provided.
         * @param content
         */
        setFlyoutRightContent: (content) => {
          set((state) => {
            state.flyoutRightContent = isDefined(content)
              ? content
              : "fileInfo";
          });
        },

        /**
         * Sets the content of the bottom flyout.
         * @param content
         */
        setFlyoutBottomContent: (content) => {
          set((state) => {
            state.flyoutBottomContent = content;
          });
        },

        /**
         * Sets the grid API object.
         * @param api
         */
        setGridApi: (api) => {
          set((state) => {
            state.gridApi = api;
          });
        },

        /**
         * Sets the selected table.
         * @param tableId
         */
        setSelectedTableId: (tableId, navigate = true) => {
          // Update store state
          set((state) => {
            state.gridApi = undefined;
            state.selectedTableId = tableId;
            state.selectedRowIds = [];
          });

          // Update route path
          const table = get().tables.find(({ id }) => id === tableId);
          navigate && navigateToTable(table?.key);
        },

        /**
         * Sets the selected file.
         * @param fileId
         */
        setSelectedFileId: (fileId) => {
          set((state) => {
            state.selectedFileId = fileId;
          });
        },

        /**
         * Intended for use only as a grid event handler.
         * Components wanting to select/deselect rows should use gridApi instead
         * @param rowIds
         */
        _setSelectedRowIds: (rowIds) => {
          set((state) => {
            state.selectedRowIds = rowIds;
          });
        },

        /**
         * Toggles advanced mode.
         * @param isAdvancedMode
         */
        setIsAdvancedMode: (isAdvancedMode) => {
          set((state) => {
            state.isAdvancedMode = isAdvancedMode;
          });
        },

        /**
         * Jumps to a column in a specified table and flashes the column in the
         * grid.
         * @param options
         * @returns A promise that resolves when the column cells have begun
         * flashing.
         * @throws An error if takes too long for the grid to load.
         */
        jumpToColumn: async ({ tableId, columnId }) => {
          /**
           * Waits until that table is selected and the grid API is ready.
           * @returns A promise that resolves when the grid API is ready.
           */
          const waitUntilGridReady = () => {
            const start = Date.now();
            const timeout = 1000;

            return new Promise<void>((resolve, reject) => {
              const interval = setInterval(() => {
                const selectedTableId = get().selectedTableId;
                const gridApi = get().gridApi;

                // Check if grid API is ready
                if (
                  selectedTableId === tableId &&
                  gridApi?.getGridId() === tableId
                ) {
                  clearInterval(interval);
                  resolve();
                }
                // Check if timeout has been exceeded
                else if (Date.now() - start > timeout) {
                  clearInterval(interval);
                  reject(new Error("Timeout exceeded"));
                }
              }, 10);
            });
          };

          // Select the table if unselected
          if (get().selectedTableId !== tableId) {
            get().setSelectedTableId(tableId);
          }

          // Wait for the grid API to be ready
          await waitUntilGridReady();

          // Scroll to the column
          const gridApi = get().gridApi;
          gridApi?.ensureColumnVisible(columnId, "start");

          // Flash column cells a tick after the scroll to complete
          setTimeout(() => {
            gridApi?.flashCells({ columns: [columnId] });
          }, 0);
        },

        /**
         * Sets the formula for a cell at a specified address.
         * @param address
         * @param newFormula
         * Creates a new data table.
         * @param options.name
         * @param options.key
         * @param options.folder
         * @returns The table ID.
         */
        createDataTable: (options) => {
          const { name, key, folder } = options;
          const sideEffects = new SideEffects(engine, get, set);

          return enqueueAction({
            details: {
              name: "createDataTable",
              args: options,
            },

            // We can't update state optimistically for this action
            onEnqueue: () => undefined,

            onDequeue: async () => {
              // Persist changes to the server
              const table = await DjangoClient.createDataTable({
                name,
                key,
                folder,
                projectId: project.id,
                sideEffects: sideEffects.serialize(),
              });

              const formattedTable = {
                id: table.id,
                kind: "data" as const,
                key,
                name,
                description: table.description,
                date_created: table.date_created,
                user: table.user,
                folder: table.folder,
                hidden: table.hidden,
                toolVersion: null,
                columnGroups: table.column_groups,
                columns: table.columns,
                rows: table.rows.map((row) => ({
                  id: row.id,
                  index: row.index,
                  editable: row.editable,
                  joinable: row.joinable,
                  task: null,
                  toolVersion: null,
                  resourceTierId: null,
                  cells: table.columns.map((column) => ({
                    isCustomFormula: false,
                    formula: column.default_formula ?? "",
                    value: null,
                  })),
                })),
              };

              // Update store state
              set((state) => {
                state.tables.push(formattedTable);
              });

              // Register new table, columns and rows with engine
              engine.addSheet(key);
              const sheetId = engine.getSheetId(key);
              assert(isDefined(sheetId));
              engine.addRows(sheetId, [0, table.rows.length]);
              engine.addColumns(sheetId, [0, table.columns.length]);
              engine.setSheetContent(
                sheetId,
                formattedTable.rows.map((row) =>
                  row.cells.map((cell) => cell.formula),
                ),
              );

              // Update cell values
              set((state) => {
                const table = state.tables.find(
                  ({ id }) => id === formattedTable.id,
                );
                assert(isDefined(table));
                table.rows.forEach((row, rowIndex) => {
                  row.cells.forEach((cell, colIndex) => {
                    cell.value = engine.getCellValue({
                      sheet: sheetId,
                      col: colIndex,
                      row: rowIndex,
                    });
                  });
                });
              });

              return table.id;
            },
          });
        },

        /**
         * Updates a data table.
         * @param options.tableId
         * @param options.name
         * @param options.description
         * @param options.key
         * @param options.folder
         * @param options.hidden
         * @returns The table ID.
         */
        updateDataTable: (options) => {
          const { tableId, name, description, key, folder, hidden } = options;
          const sideEffects = new SideEffects(engine, get, set);

          return enqueueAction({
            details: {
              name: "updateDataTable",
              args: options,
            },

            onEnqueue: () => {
              // Parse table ID
              const table = get().tables.find(({ id }) => id === tableId);
              assert(isDefined(table));
              const sheetId = engine.getSheetId(table.key);
              assert(isDefined(sheetId));

              // Update store state
              set((state) => {
                const table = state.tables.find(({ id }) => id === tableId);
                assert(isDefined(table));

                if (isDefined(name)) {
                  table.name = name;
                }

                if (isDefined(description)) {
                  table.description = description;
                }

                if (isDefined(key)) {
                  table.key = key;
                  navigateToTable(key, { replace: true });
                }

                if (isDefined(folder)) {
                  table.folder = folder;
                }

                if (isDefined(hidden)) {
                  table.hidden = hidden;
                }
              });

              // Register new table key with engine and recalculate stale row
              // identifiers
              if (isDefined(key)) {
                engine.renameSheet(sheetId, key);
                recalculateCellValues(engine, (_address, _formula, value) => {
                  return (
                    value instanceof RowIdentifier &&
                    value.tableKey === table.key
                  );
                });
              }

              // Apply side-effects
              sideEffects.init();

              if (isDefined(key)) {
                const updatedCells = sideEffects.updateRowIdentifiers(
                  table.key,
                  key,
                );
                updatedCells.forEach((cell) => {
                  engine.setCellContents(cell.address, cell.newFormula);
                });
              }

              sideEffects.applyOnStoreState();
            },

            onDequeue: async () => {
              // Persist changes to the server
              await DjangoClient.updateDataTable({
                tableId,
                name,
                description,
                key,
                folder,
                hidden,
                sideEffects: sideEffects.serialize(),
              });
              return tableId;
            },
          });
        },

        /**
         * Updates a analysis table.
         * @param options.tableId
         * @param options.name
         * @param options.description
         * @param options.key
         * @param options.folder
         * @param options.hidden
         * @returns The table ID.
         */
        updateAnalysisTable: (options) => {
          const { tableId, name, description, key, folder, hidden } = options;
          const sideEffects = new SideEffects(engine, get, set);

          return enqueueAction({
            details: {
              name: "updateAnalysisTable",
              args: options,
            },

            onEnqueue: () => {
              // Parse table ID
              const table = get().tables.find(({ id }) => id === tableId);
              assert(isDefined(table));
              const sheetId = engine.getSheetId(table.key);
              assert(isDefined(sheetId));

              // Update store state
              set((state) => {
                const table = state.tables.find(({ id }) => id === tableId);
                assert(isDefined(table));

                if (isDefined(name)) {
                  table.name = name;
                }

                if (isDefined(description)) {
                  table.description = description;
                }

                if (isDefined(key)) {
                  table.key = key;
                  navigateToTable(key, { replace: true });
                }

                if (isDefined(folder)) {
                  table.folder = folder;
                }

                if (isDefined(hidden)) {
                  table.hidden = hidden;
                }
              });

              // Register new table key with engine and recalculate stale row
              // identifiers
              if (isDefined(key)) {
                engine.renameSheet(sheetId, key);
                recalculateCellValues(engine, (_address, _formula, value) => {
                  return (
                    value instanceof RowIdentifier &&
                    value.tableKey === table.key
                  );
                });
              }

              // Apply side-effects
              sideEffects.init();

              if (isDefined(key)) {
                const updatedCells = sideEffects.updateRowIdentifiers(
                  table.key,
                  key,
                );
                updatedCells.forEach((cell) => {
                  engine.setCellContents(cell.address, cell.newFormula);
                });
              }

              sideEffects.applyOnStoreState();
            },

            onDequeue: async () => {
              // Persist changes to the server
              await DjangoClient.updateAnalysisTable({
                tableId,
                name,
                description,
                key,
                folder,
                hidden,
                sideEffects: sideEffects.serialize(),
              });
              return tableId;
            },
          });
        },

        /**
         * Updates an analysis table row.
         * @param options.tableId
         * @param options.rowId
         * @param options.toolVersionId If the ID is `null`, the tool version
         * will be reset to the table default.
         * @param options.resourceTierId If the ID is `null`, the resource tier
         * will be reset to the tool version default.
         * @returns The row ID.
         */
        updateAnalysisTableRow: (options) => {
          const { tableId, rowId, toolVersion, resourceTierId, isJoinable } =
            options;
          return enqueueAction({
            details: {
              name: "updateAnalysisTableRow",
              args: options,
            },

            onEnqueue: () => {
              // Parse sheet ID
              const table = get().tables.find(({ id }) => id === tableId);
              assert(isDefined(table));
              const sheetId = engine.getSheetId(table.key);
              assert(isDefined(sheetId));

              // Update store state
              set((state) => {
                const table = state.tables.find(({ id }) => id === tableId);
                assert(isDefined(table));
                const sheetId = engine.getSheetId(table.key);
                assert(isDefined(sheetId));
                const row = table.rows.find(({ id }) => id === rowId);
                assert(isDefined(row));

                if (isDefined(toolVersion)) {
                  row.toolVersion = toolVersion;
                }

                if (isDefined(resourceTierId)) {
                  row.resourceTierId = resourceTierId;
                }

                if (isDefined(isJoinable)) {
                  row.joinable = isJoinable;
                }
              });

              // Recalculate formulas referencing the row
              if (isDefined(isJoinable)) {
                recalculateCellValues(engine, (address, formula) => {
                  // If the cell does not contain a formula, do nothing
                  if (formula === undefined) {
                    return false;
                  }

                  let shouldRecalculate = false;
                  const { ast } = engine.formulaToAst(formula, address.sheet);

                  traverseAst(ast, (node) => {
                    if (
                      node.type === AstNodeType.COLUMN_RANGE &&
                      (node.start.isColumnRelative() ||
                        node.start.sheet === sheetId)
                    ) {
                      shouldRecalculate = true;
                    }
                  });

                  return shouldRecalculate;
                });
              }
            },

            onDequeue: async () => {
              // Persist changes to the server
              await DjangoClient.updateAnalysisTableRow({
                rowId,
                tool_version: toolVersion !== null ? toolVersion?.id : null,
                resource_tier: resourceTierId,
                joinable: isJoinable,
                sideEffects: new SideEffects(engine, get, set).serialize(),
              });
              return rowId;
            },
          });
        },

        /**
         * Creates a new analysis table.
         * @param options.toolVersionId
         * @param options.name
         * @param options.key
         * @param options.folder
         * @returns The table ID.
         */
        createAnalysisTable: (options) => {
          const { toolVersionId, name, key, sourceTables, paramMap, folder } =
            options;
          const sideEffects = new SideEffects(engine, get, set);

          return enqueueAction({
            details: {
              name: "createAnalysisTable",
              args: options,
            },

            // We can't update state optimistically for this action
            onEnqueue: () => undefined,

            onDequeue: async () => {
              // Persist changes to the server
              const table = await DjangoClient.createAnalysisTable({
                toolVersionId,
                name,
                key,
                folder,
                projectId: project.id,
                sourceTables,
                paramMap,
                sideEffects: sideEffects.serialize(),
              });

              const formattedTable = {
                id: table.id,
                kind: "analysis" as const,
                key,
                name,
                description: table.description,
                date_created: table.date_created,
                user: table.user,
                folder: table.folder,
                hidden: table.hidden,
                toolVersion: table.tool_version,
                columnGroups: table.column_groups,
                columns: table.columns,
                rows: table.rows.map((row) => ({
                  id: row.id,
                  index: row.index,
                  editable: row.editable,
                  joinable: row.joinable,
                  task: null,
                  toolVersion: table.tool_version,
                  resourceTierId: null,
                  cells: table.columns.map((column) => ({
                    isCustomFormula: false,
                    formula: column.default_formula ?? "",
                    value: null,
                  })),
                })),
              };

              // Update store state
              set((state) => {
                state.tables.push(formattedTable);
              });

              // Register new table, columns and rows with engine
              engine.addSheet(key);
              const sheetId = engine.getSheetId(key);
              assert(isDefined(sheetId));
              engine.addRows(sheetId, [0, table.rows.length]);
              engine.addColumns(sheetId, [0, table.columns.length]);
              engine.setSheetContent(
                sheetId,
                formattedTable.rows.map((row) =>
                  row.cells.map((cell) => cell.formula),
                ),
              );

              // Update cell values
              set((state) => {
                const table = state.tables.find(
                  ({ id }) => id === formattedTable.id,
                );
                assert(isDefined(table));
                table.rows.forEach((row, rowIndex) => {
                  row.cells.forEach((cell, colIndex) => {
                    cell.value = engine.getCellValue({
                      sheet: sheetId,
                      col: colIndex,
                      row: rowIndex,
                    });
                  });
                });
              });
              return table.id;
            },
          });
        },

        /**
         * Creates a new data table column.
         * @param options
         * @returns The column ID.
         */
        createColumn: (options) => {
          const { tableId, name, editable, defaultFormula, definition } =
            options;
          const sideEffects = new SideEffects(engine, get, set);

          return enqueueAction({
            details: {
              name: "createColumn",
              args: options,
            },

            // We can't update state optimistically for this action
            onEnqueue: () => undefined,

            onDequeue: async () => {
              // Parse table ID
              const table = get().tables.find(({ id }) => id === tableId);
              assert(isDefined(table));
              const sheetId = engine.getSheetId(table.key);
              assert(isDefined(sheetId));

              // Persist changes to the server
              const column = await DjangoClient.createDataTableColumn({
                projectId: project.id,
                tableId,
                name,
                editable,
                defaultFormula,
                definition,
                sideEffects: sideEffects.serialize(),
              });

              // Update store state
              set((state) => {
                const table = state.tables.find(({ id }) => id === tableId);
                assert(isDefined(table));
                table.columns.push(column);
                table.rows.forEach((row) => {
                  row.cells.push({
                    isCustomFormula: false,
                    formula: defaultFormula ?? "",
                    value: null,
                  });
                });
              });

              // Register new column with engine
              const colIndex = table.columns.length;
              engine.addColumns(sheetId, [colIndex, 1]);
              engine.setCellContents(
                { sheet: sheetId, col: colIndex, row: 0 },
                new Array(table.rows.length).fill([defaultFormula]),
              );

              return column.id;
            },
          });
        },

        /**
         * Adds a specified number of rows to a table.
         * @param options.tableId
         * @param options.numRows
         * @returns An array of row IDs.
         */
        createRows: (options) => {
          const { tableId, numRows } = options;
          const sideEffects = new SideEffects(engine, get, set);

          return enqueueAction({
            details: {
              name: "createRows",
              args: options,
            },

            // We can't update state optimistically for this action
            onEnqueue: () => undefined,

            onDequeue: async () => {
              // Parse table ID
              const table = get().tables.find(({ id }) => id === tableId);
              assert(isDefined(table));
              const sheetId = engine.getSheetId(table.key);
              assert(isDefined(sheetId));
              const defaultFormulas = table.columns.map(
                (column) => column.default_formula,
              );

              // Persist changes to the server
              const rows = await DjangoClient.createRowsBulk({
                projectId: project.id,
                tableId,
                numRows,
                tableKind: table.kind,
                sideEffects: sideEffects.serialize(),
              });

              // Update store state
              set((state) => {
                const table = state.tables.find(({ id }) => id === tableId);
                assert(isDefined(table));
                rows.forEach((row) => {
                  table.rows.push({
                    ...row,
                    task: null,
                    toolVersion: table.toolVersion,
                    resourceTierId: null,
                    cells: defaultFormulas.map((formula) => ({
                      isCustomFormula: false,
                      formula: formula ?? "",
                      value: null,
                    })),
                  });
                });
              });

              // Register new rows with engine
              const rowIndex = table.rows.length;
              engine.addRows(sheetId, [rowIndex, numRows]);
              engine.setCellContents(
                { sheet: sheetId, col: 0, row: rowIndex },
                new Array(numRows).fill(defaultFormulas),
              );

              return rows.map((row) => row.id);
            },
          });
        },

        /**
         * Sets the formula for a cell at a specified address.
         * @param options
         */
        setCellFormula: async (options) => {
          const { address, formula } = options;
          const sideEffects = new SideEffects(engine, get, set);

          await enqueueAction({
            details: {
              name: "setCellFormula",
              args: options,
            },

            onEnqueue: () => {
              // Parse cell address
              const { tableId, columnId, rowId } = address;
              const table = get().tables.find(({ id }) => id === tableId);
              assert(isDefined(table));
              const sheetId = engine.getSheetId(table.key);
              assert(isDefined(sheetId));
              const colIndex = table.columns.findIndex(
                ({ id }) => id === columnId,
              );
              const rowIndex = table.rows.findIndex(({ id }) => id === rowId);

              // Update store state
              set((state) => {
                const table = state.tables.find(({ id }) => id === tableId);
                assert(isDefined(table));
                const cell = table.rows[rowIndex].cells[colIndex];
                cell.isCustomFormula = true;
                cell.formula = formula;
              });

              // Trigger engine to recalculate cell values
              engine.setCellContents(
                { sheet: sheetId, col: colIndex, row: rowIndex },
                formula,
              );

              // Apply side-effects
              sideEffects.init();
              sideEffects.applyOnStoreState();
            },

            onDequeue: () => {
              const { tableId } = address;
              const table = get().tables.find(({ id }) => id === tableId);
              assert(isDefined(table));

              return DjangoClient.updateCellFormula({
                projectId: project.id,
                address,
                formula,
                tableKind: table.kind,
                sideEffects: sideEffects.serialize(),
              });
            },
          });
        },

        joinDataTable: (options) => {
          const {
            tableId,
            headerName,
            sourceTableId,
            leftColumnId,
            rightColumnId,
            sourceColumnIds,
          } = options;
          return enqueueAction({
            details: {
              name: "joinDataTable",
              args: options,
            },

            // We can't update state optimistically for this action
            onEnqueue: () => undefined,

            onDequeue: async () => {
              // Parse table ID
              const table = get().tables.find((table) => table.id === tableId);
              assert(isDefined(table));
              const sheetId = engine.getSheetId(table.key);
              assert(isDefined(sheetId));

              // Persist changes to server
              await DjangoClient.joinDataTable({
                tableId,
                headerName,
                sourceTableId,
                leftColumnId,
                rightColumnId,
                sourceColumnIds,
                sideEffects: new SideEffects(engine, get, set).serialize(),
              });

              // Refetch columns
              const data = await DjangoClient.getTableData({
                projectId: project.id,
              });

              const columnGroups = data.find((table) => table.id === tableId)
                ?.column_groups;

              const newColumns = data
                .find((table) => table.id === tableId)
                ?.columns.slice(table.columns.length);

              // Update store state
              set((state) => {
                const table = state.tables.find(({ id }) => id === tableId);
                assert(isDefined(table));
                table.columnGroups = columnGroups ?? [];
                table.columns.push(...(newColumns ?? []));
                table.rows.forEach((row) => {
                  newColumns?.forEach((column) => {
                    row.cells.push({
                      isCustomFormula: false,
                      formula: column.default_formula ?? "",
                      value: null,
                    });
                  });
                });
              });

              // Notify engine of new columns
              newColumns?.forEach((column, idx) => {
                const colIndex = idx + table.columns.length;
                engine.addColumns(sheetId, [colIndex, 1]);
                engine.setCellContents(
                  { sheet: sheetId, col: colIndex, row: 0 },
                  new Array(table.rows.length).fill([column.default_formula]),
                );
              });

              return (newColumns ?? []).map(({ id }) => id);
            },
          });
        },

        linkDataTable: (options) => {
          const { tableId, headerName, sourceTableId, sourceColumnIds } =
            options;
          return enqueueAction({
            details: {
              name: "linkDataTable",
              args: options,
            },

            // We can't update state optimistically for this action
            onEnqueue: () => undefined,

            onDequeue: async () => {
              // Parse table ID
              const table = get().tables.find((table) => table.id === tableId);
              assert(isDefined(table));
              const sheetId = engine.getSheetId(table.key);
              assert(isDefined(sheetId));

              // Persist changes to server
              await DjangoClient.linkDataTable({
                tableId,
                headerName,
                sourceTableId,
                sourceColumnIds,
                sideEffects: new SideEffects(engine, get, set).serialize(),
              });

              // Refetch columns
              const data = await DjangoClient.getTableData({
                projectId: project.id,
              });

              const columnGroups = data.find((table) => table.id === tableId)
                ?.column_groups;

              const newColumns = data
                .find((table) => table.id === tableId)
                ?.columns.slice(table.columns.length);

              // Update store state
              set((state) => {
                const table = state.tables.find(({ id }) => id === tableId);
                assert(isDefined(table));
                table.columnGroups = columnGroups ?? [];
                table.columns.push(...(newColumns ?? []));
                table.rows.forEach((row) => {
                  newColumns?.forEach((column) => {
                    row.cells.push({
                      isCustomFormula: false,
                      formula: column.default_formula ?? "",
                      value: null,
                    });
                  });
                });
              });

              // Notify engine of new columns
              newColumns?.forEach((column, idx) => {
                const colIndex = idx + table.columns.length;
                engine.addColumns(sheetId, [colIndex, 1]);
                engine.setCellContents(
                  { sheet: sheetId, col: colIndex, row: 0 },
                  new Array(table.rows.length).fill([column.default_formula]),
                );
              });

              return (newColumns ?? []).map(({ id }) => id);
            },
          });
        },

        joinAnalysisTable: (options) => {
          const {
            tableId,
            headerName,
            sourceTableId,
            leftColumnId,
            rightColumnId,
            sourceColumnIds,
          } = options;
          return enqueueAction({
            details: {
              name: "joinAnalysisTable",
              args: options,
            },

            // We can't update state optimistically for this action
            onEnqueue: () => undefined,

            onDequeue: async () => {
              // Parse table ID
              const table = get().tables.find((table) => table.id === tableId);
              assert(isDefined(table));
              const sheetId = engine.getSheetId(table.key);
              assert(isDefined(sheetId));

              // Persist changes to server
              await DjangoClient.joinAnalysisTable({
                tableId,
                headerName,
                sourceTableId,
                leftColumnId,
                rightColumnId,
                sourceColumnIds,
                sideEffects: new SideEffects(engine, get, set).serialize(),
              });

              // Refetch columns
              const data = await DjangoClient.getTableData({
                projectId: project.id,
              });

              const columnGroups = data.find((table) => table.id === tableId)
                ?.column_groups;

              const newColumns = data
                .find((table) => table.id === tableId)
                ?.columns.slice(table.columns.length);

              // Update store state
              set((state) => {
                const table = state.tables.find(({ id }) => id === tableId);
                assert(isDefined(table));
                table.columnGroups = columnGroups ?? [];
                table.columns.push(...(newColumns ?? []));
                table.rows.forEach((row) => {
                  newColumns?.forEach((column) => {
                    row.cells.push({
                      isCustomFormula: false,
                      formula: column.default_formula ?? "",
                      value: null,
                    });
                  });
                });
              });

              // Notify engine of new columns
              newColumns?.forEach((column, idx) => {
                const colIndex = idx + table.columns.length;
                engine.addColumns(sheetId, [colIndex, 1]);
                engine.setCellContents(
                  { sheet: sheetId, col: colIndex, row: 0 },
                  new Array(table.rows.length).fill([column.default_formula]),
                );
              });

              return (newColumns ?? []).map(({ id }) => id);
            },
          });
        },

        deleteTable: (options) => {
          const { tableId, fileIds } = options;
          const sideEffects = new SideEffects(engine, get, set);
          const table = get().tables.find((table) => table.id === tableId);

          return enqueueAction({
            details: {
              name: "deleteTable",
              args: options,
            },

            onEnqueue: () => {
              // Parse table ID
              assert(isDefined(table));
              const sheetId = engine.getSheetId(table.key);
              assert(isDefined(sheetId));

              // Update store state
              set((state) => {
                state.tables = state.tables.filter(
                  (table) => table.id !== tableId,
                );
              });

              // Notify engine of the removed table and recalculate stale
              // row identifiers
              engine.removeSheet(sheetId);
              recalculateCellValues(engine, (_address, _formula, value) => {
                return (
                  value instanceof RowIdentifier && value.tableKey === table.key
                );
              });

              // Apply side-effects
              sideEffects.init();
              sideEffects.applyOnStoreState();
            },

            onDequeue: async () => {
              // Persist changes to server
              assert(isDefined(table));
              await DjangoClient.deleteTable({
                tableKind: table.kind,
                tableId: table.id,
                fileIds,
                sideEffects: sideEffects.serialize(),
              });
              // Reroute user
              get().setSelectedTableId(undefined);
              return tableId;
            },
          });
        },

        deleteDataTableColumnGroup: (options) => {
          const { tableId, groupId } = options;
          const sideEffects = new SideEffects(engine, get, set);

          return enqueueAction({
            details: {
              name: "deleteDataTableColumnGroup",
              args: options,
            },

            onEnqueue: () => {
              // Parse table ID
              const table = get().tables.find((table) => table.id === tableId);
              assert(isDefined(table));
              const sheetId = engine.getSheetId(table.key);
              assert(isDefined(sheetId));

              const columnIndices: number[] = [];
              table.columns.forEach((column, idx) => {
                if (column.group === groupId) {
                  columnIndices.push(idx);
                }
              });

              // Update store state
              set((state) => {
                const table = state.tables.find(({ id }) => id === tableId);
                assert(isDefined(table));

                table.columnGroups = table.columnGroups.filter(
                  ({ id }) => id !== groupId,
                );

                table.columns = table.columns.filter(
                  ({ group }) => group !== groupId,
                );

                table.columns.forEach((column, idx) => {
                  column.order = idx;
                });

                table.rows.forEach((row) => {
                  row.cells = row.cells.filter((_, idx) => {
                    return !columnIndices.includes(idx);
                  });
                });
              });

              // Notify engine of the removed columns
              engine.removeColumns(
                sheetId,
                ...columnIndices.map(
                  (index) => [index, 1] satisfies [number, number],
                ),
              );

              // Apply side-effects
              sideEffects.init();
              sideEffects.applyOnStoreState();
            },

            onDequeue: () => {
              // Persist changes to server
              return DjangoClient.deleteDataTableColumnGroup({
                groupId,
                sideEffects: sideEffects.serialize(),
              });
            },
          });
        },

        deleteDataTableColumn: (options) => {
          const { tableId, columnId, fileIds } = options;
          const sideEffects = new SideEffects(engine, get, set);

          return enqueueAction({
            details: {
              name: "deleteDataTableColumn",
              args: options,
            },

            onEnqueue: () => {
              // Parse table ID
              const table = get().tables.find((table) => table.id === tableId);
              assert(isDefined(table));
              const sheetId = engine.getSheetId(table.key);
              assert(isDefined(sheetId));
              const columnIndex = table.columns.findIndex(
                (column) => column.id === columnId,
              );

              // Update store state
              set((state) => {
                const table = state.tables.find(({ id }) => id === tableId);
                assert(isDefined(table));
                table.columns = table.columns.filter(
                  (column) => column.id !== columnId,
                );
                table.rows.forEach((row) => {
                  row.cells.splice(columnIndex, 1);
                });
              });

              // Notify engine of the removed column
              engine.removeColumns(sheetId, [columnIndex, 1]);

              // Apply side-effects
              sideEffects.init();
              sideEffects.applyOnStoreState();
            },

            onDequeue: () => {
              // Persist changes to server
              return DjangoClient.deleteDataTableColumn({
                columnId,
                fileIds,
                sideEffects: sideEffects.serialize(),
              });
            },
          });
        },

        deleteRows: (options) => {
          const { tableId, rows } = options;
          const sideEffects = new SideEffects(engine, get, set);

          return enqueueAction({
            details: {
              name: "deleteRows",
              args: options,
            },

            onEnqueue: () => {
              // Parse table ID
              const table = get().tables.find((table) => table.id === tableId);
              assert(isDefined(table));
              const sheetId = engine.getSheetId(table.key);
              assert(isDefined(sheetId));
              const rowIds = rows.map((r) => r.id);
              const rowIndices = rowIds.map((rowId) => {
                return table.rows.findIndex((row) => row.id === rowId);
              });

              // Update store state
              set((state) => {
                const table = state.tables.find(({ id }) => id === tableId);
                assert(isDefined(table));
                table.rows = table.rows.filter(
                  (row) => !rowIds.includes(row.id),
                );
              });

              // Notify engine of removed rows
              engine.removeRows(
                sheetId,
                ...rowIndices.map(
                  (rowIndex) => [rowIndex, 1] satisfies [number, number],
                ),
              );

              // Apply side-effects
              sideEffects.init();
              sideEffects.applyOnStoreState();
            },

            onDequeue: () => {
              // Persist changes to server
              const table = get().tables.find((table) => table.id === tableId);
              assert(isDefined(table));
              return DjangoClient.deleteRows({
                tableKind: table.kind,
                rows: rows.map((row) => ({
                  id: row.id,
                  fileIds: row.fileIds,
                  sideEffects: sideEffects.serialize(),
                })),
              });
            },
          });
        },

        renameColumnGroup: (options) => {
          const { tableId, groupId, newName } = options;
          const sideEffects = new SideEffects(engine, get, set);

          return enqueueAction({
            details: {
              name: "renameColumnGroup",
              args: options,
            },

            onEnqueue: () => {
              // Update store state
              set((state) => {
                const table = state.tables.find(({ id }) => id === tableId);
                const group = table?.columnGroups.find(
                  ({ id }) => id === groupId,
                );
                if (isDefined(group)) {
                  group.name = newName;
                }
              });
            },

            onDequeue: async () => {
              // Persist changes to server
              const table = get().tables.find((table) => table.id === tableId);
              assert(isDefined(table));
              await DjangoClient.updateColumnGroup({
                tableKind: table.kind,
                groupId,
                name: newName,
                sideEffects: sideEffects.serialize(),
              });
              return newName;
            },
          });
        },

        /**
         * Updates a columns name, definition attributes and default formula.
         * @param options
         * @returns The column ID.
         */
        updateColumn: (options) => {
          const { tableId, columnId, name, defaultFormula, definition } =
            options;
          const sideEffects = new SideEffects(engine, get, set);

          return enqueueAction({
            details: {
              name: "updateColumn",
              args: options,
            },

            // We can't update state optimistically for this action
            onEnqueue: () => undefined,

            onDequeue: async () => {
              // Parse table ID
              const table = get().tables.find(({ id }) => id === tableId);
              assert(isDefined(table));
              const sheetId = engine.getSheetId(table.key);
              assert(isDefined(sheetId));
              const colIdx = table.columns.findIndex(
                ({ id }) => id === columnId,
              );
              assert(colIdx >= 0);
              const column = table.columns[colIdx];
              const isDefaultFormulaChanged =
                column.default_formula !== defaultFormula;

              // Register new cell formulas with engine
              if (isDefaultFormulaChanged) {
                engine.batch(() => {
                  table.rows.forEach((row, rowIdx) => {
                    const cell = row.cells[colIdx];
                    if (!cell.isCustomFormula) {
                      engine.setCellContents(
                        { sheet: sheetId, col: colIdx, row: rowIdx },
                        defaultFormula,
                      );
                    }
                  });
                });
              }

              // Update store state
              set((state) => {
                const table = state.tables.find(({ id }) => id === tableId);
                assert(isDefined(table));

                const column = table.columns[colIdx];

                if (isDefined(name)) {
                  column.name = name;
                }

                if (isDefined(defaultFormula)) {
                  column.default_formula = defaultFormula;
                }

                if (isDefined(definition)) {
                  column.definition = definition;
                }

                table.rows.forEach((row) => {
                  const cell = row.cells[colIdx];
                  if (!cell.isCustomFormula) {
                    cell.formula = column.default_formula ?? "";
                  }
                });
              });

              // Apply side-effects, skipping effects related to the
              // updated column
              sideEffects.init(
                (e) => e.type === "column" && e.columnId === columnId,
              );
              sideEffects.applyOnStoreState();

              // Persist changes to the server
              await DjangoClient.updateColumn({
                tableKind: table.kind,
                columnId,
                name,
                defaultFormula,
                definition: definition
                  ? { ...definition, project: project.id }
                  : undefined,
                sideEffects: sideEffects.serialize(),
              });

              return columnId;
            },
          });
        },

        renameColumn: (options) => {
          const { tableId, columnId, newName } = options;
          const sideEffects = new SideEffects(engine, get, set);

          return enqueueAction({
            details: {
              name: "renameColumn",
              args: options,
            },

            onEnqueue: () => {
              // Update store state
              set((state) => {
                const table = state.tables.find(
                  (table) => table.id === tableId,
                );
                const column = table?.columns.find(
                  (column) => column.id === columnId,
                );

                if (isDefined(column)) {
                  column.name = newName;
                }
              });
            },

            onDequeue: async () => {
              // Persist changes to server
              const table = get().tables.find((table) => table.id === tableId);
              assert(isDefined(table));
              await DjangoClient.updateColumn({
                tableKind: table.kind,
                columnId,
                name: newName,
                sideEffects: sideEffects.serialize(),
              });
              return newName;
            },
          });
        },

        resizeColumn: (options) => {
          const { tableId, columnId, newWidth, persistChange } = options;
          const sideEffects = new SideEffects(engine, get, set);

          return enqueueAction({
            details: {
              name: "resizeColumn",
              args: options,
            },

            onEnqueue: () => {
              // Update store state
              set((state) => {
                const table = state.tables.find(
                  (table) => table.id === tableId,
                );
                const column = table?.columns.find(
                  (column) => column.id === columnId,
                );

                if (isDefined(column)) {
                  column.width = newWidth;
                }
              });
            },

            onDequeue: async () => {
              if (persistChange) {
                const table = get().tables.find(
                  (table) => table.id === tableId,
                );
                assert(isDefined(table));
                await DjangoClient.updateColumn({
                  tableKind: table.kind,
                  columnId,
                  width: newWidth,
                  sideEffects: sideEffects.serialize(),
                });
              }
              return newWidth;
            },
          });
        },

        /**
         * Gets the identifier for each row where a specified file is a cell
         * value.
         * @param fileId
         * @returns The row identifiers.
         */
        getFileReferences: (fileId) => {
          const identifiers: RowIdentifier[] = [];

          get().tables.forEach((table) => {
            table.rows.forEach((row) => {
              row.cells.forEach((cell) => {
                const identifier = new RowIdentifier(table.key, row.index);

                if (
                  cell.value instanceof IdeasFile &&
                  cell.value.attrs.id === fileId
                ) {
                  identifiers.push(identifier);
                }

                if (
                  cell.value instanceof Group &&
                  cell.value.cellValues.some(
                    (value) =>
                      value instanceof IdeasFile && value.attrs.id === fileId,
                  )
                ) {
                  identifiers.push(identifier);
                }
              });
            });
          });

          return uniqWith(
            identifiers,
            (a, b) => a.tableKey === b.tableKey && a.rowIndex === b.rowIndex,
          );
        },

        deleteFile: (options) => {
          const { fileId, mode } = options;
          const dateDeleted = new Date().toISOString();
          const sideEffects = new SideEffects(engine, get, set);

          return enqueueAction({
            details: {
              name: "deleteFile",
              args: options,
            },

            onEnqueue: () => {
              const file = get().fileMap.filesById[fileId];

              // Only update local state if the file is referenced in the grid
              if (!isDefined(file) || file === IdeasFileNotFound) {
                return;
              }

              // Cancel upload if in-progress
              cancelFileUploads([fileId]);

              // Update store state
              set((state) => {
                state.fileMap.filesById[fileId] = IdeasFileNotFound;
                state.fileMap.filesByReferenceId[file.reference_id] =
                  IdeasFileNotFound;
              });

              // Apply side-effects
              sideEffects.init();
              const updatedCells = sideEffects.deleteFiles([file.reference_id]);
              sideEffects.applyOnStoreState();

              // Notify engine of cells with changed formulas
              updatedCells.forEach((cell) => {
                engine.setCellContents(cell.address, cell.newFormula);
              });

              // Recalculate stale formulas referencing the deleted file
              recalculateCellValues(engine, (address, formula) => {
                // If the cell does not contain a formula, do nothing
                if (formula === undefined) {
                  return false;
                }

                let shouldRecalculate = false;
                const { ast } = engine.formulaToAst(formula, address.sheet);

                traverseAst(ast, (node) => {
                  if (
                    node.type === AstNodeType.FUNCTION_CALL &&
                    node.procedureName === "FILE" &&
                    node.args[0].type === AstNodeType.STRING &&
                    node.args[0].value === file.reference_id
                  ) {
                    shouldRecalculate = true;
                  }
                });

                return shouldRecalculate;
              });
            },

            onDequeue: () => {
              return DjangoClient.deleteFile({
                fileId,
                dateDeleted,
                mode,
                sideEffects: sideEffects.serialize(),
              });
            },
          });
        },

        setIdentifierPositions: (options) => {
          const { tableId, columnIds } = options;
          const sideEffects = new SideEffects(engine, get, set);

          return enqueueAction({
            details: {
              name: "setIdentifierPositions",
              args: options,
            },

            onEnqueue: () => {
              // Update store state
              set((state) => {
                const table = state.tables.find(({ id }) => id === tableId);
                assert(isDefined(table));
                table.columns.forEach((column) => {
                  const identifierPosition = columnIds.indexOf(column.id);
                  column.identifier_position =
                    identifierPosition !== -1 ? identifierPosition : null;
                });
              });

              // Apply side-effects
              sideEffects.init();
              sideEffects.applyOnStoreState();
            },

            onDequeue: async () => {
              // Persist changes to server
              const table = get().tables.find((table) => table.id === tableId);
              assert(isDefined(table));
              await DjangoClient.reorderIdentifiers({
                tableId: table.id,
                tableKind: table.kind,
                columnIds,
                sideEffects: sideEffects.serialize(),
              });
              return columnIds;
            },
          });
        },

        unassignFile: (options) => {
          const { fileId, address } = options;
          const sideEffects = new SideEffects(engine, get, set);

          return enqueueAction({
            details: {
              name: "unassignFile",
              args: options,
            },

            onEnqueue: () => {
              const file = get().fileMap.filesById[fileId];
              assert(isDefined(file) && !(file === IdeasFileNotFound));

              // Apply side-effects
              sideEffects.init();
              sideEffects.deleteFiles([file.reference_id], address);
              sideEffects.applyOnStoreState();
              sideEffects.applyOnEngineState();
            },

            onDequeue: async () => {
              await DjangoClient.applySideEffects({
                projectId: get().project.id,
                sideEffects: sideEffects.serialize(),
              });
              return fileId;
            },
          });
        },

        createSeries: (options) => {
          const { seriesFileId, seriesFileName, seriesMemberFileIds } = options;
          const sideEffects = new SideEffects(engine, get, set);

          return enqueueAction({
            details: {
              name: "createSeries",
              args: options,
            },

            onEnqueue: () => {
              sideEffects.init();
              const referencedIds = seriesMemberFileIds
                .map((fileId) => {
                  const file = get().fileMap.filesById[fileId];
                  return file !== undefined && !(file === IdeasFileNotFound)
                    ? file.reference_id
                    : undefined;
                })
                .filter(isDefined);
              sideEffects.deleteFiles(referencedIds);
              sideEffects.applyOnStoreState();
              sideEffects.applyOnEngineState();
            },

            onDequeue: () => {
              return DjangoClient.createSeries({
                seriesFileId,
                seriesFileName,
                seriesMemberFileIds,
                sideEffects: sideEffects.serialize(),
              });
            },
          });
        },

        breakSeries: (options) => {
          const { fileId } = options;
          const sideEffects = new SideEffects(engine, get, set);

          return enqueueAction({
            details: {
              name: "breakSeries",
              args: options,
            },

            onEnqueue: () => {
              // Calculate side-effects
              sideEffects.init();
              const file = get().fileMap.filesById[fileId];
              if (isDefined(file) && !(file === IdeasFileNotFound)) {
                sideEffects.deleteFiles([file.reference_id]);
              }
              sideEffects.applyOnStoreState();
              sideEffects.applyOnEngineState();
            },

            onDequeue: () => {
              return DjangoClient.breakSeries({
                fileId,
                sideEffects: sideEffects.serialize(),
              });
            },
          });
        },

        /**
         * Sets the column display order for a table.
         * @param options
         * @returns The new column order.
         */
        setColumnOrder: (options) => {
          const { tableId, columnIds } = options;
          const sideEffects = new SideEffects(engine, get, set);

          return enqueueAction({
            details: {
              name: "setColumnOrder",
              args: options,
            },

            onEnqueue: () => {
              // Parse sheet ID
              const table = get().tables.find((table) => table.id === tableId);
              assert(isDefined(table));
              const sheetId = engine.getSheetId(table.key);
              assert(isDefined(sheetId));

              // Create a log of every moved column. We have to "replay" each
              // column move individually because of a bug with
              // HyperFormula.setColumnOrder
              // (https://github.com/handsontable/hyperformula/issues/1500).
              const operations: { oldPosition: number; newPosition: number }[] =
                [];

              // Update store state
              set((state) => {
                const table = state.tables.find(({ id }) => id === tableId);
                assert(isDefined(table));
                columnIds.forEach((columnId, newPosition) => {
                  // Find the old position
                  const oldPosition = table.columns.findIndex(
                    ({ id }) => id === columnId,
                  );

                  if (newPosition !== oldPosition) {
                    // Reorder all columns
                    const [column] = table.columns.splice(oldPosition, 1);
                    table.columns.splice(newPosition, 0, column);
                    table.columns.forEach((column, idx) => {
                      column.order = idx;
                    });

                    // Reorder cells in each row
                    table.rows.forEach((row) => {
                      const [cell] = row.cells.splice(oldPosition, 1);
                      row.cells.splice(newPosition, 0, cell);
                    });

                    // Log the operation for replay on HF engine
                    operations.push({ oldPosition, newPosition });
                  }
                });
              });

              // Notify engine of each moved column
              engine.batch(() => {
                operations.forEach(({ oldPosition, newPosition }) => {
                  if (newPosition < oldPosition) {
                    engine.moveColumns(sheetId, oldPosition, 1, newPosition);
                  } else {
                    engine.moveColumns(
                      sheetId,
                      oldPosition,
                      1,
                      newPosition + 1,
                    );
                  }
                });
              });

              // // Apply side-effects
              sideEffects.init();
              sideEffects.applyOnStoreState();
            },

            onDequeue: async () => {
              // Persist changes to server
              const table = get().tables.find((table) => table.id === tableId);
              assert(isDefined(table));
              await DjangoClient.reorderColumns({
                tableId: table.id,
                tableKind: table.kind,
                columnIds,
                sideEffects: sideEffects.serialize(),
              });
              return columnIds;
            },
          });
        },

        pinColumn: (options) => {
          const { tableId, columnId } = options;
          const sideEffects = new SideEffects(engine, get, set);

          return enqueueAction({
            details: {
              name: "pinColumn",
              args: options,
            },

            onEnqueue: () => {
              // Calculate column's new position
              const table = get().tables.find(({ id }) => id === tableId);
              assert(isDefined(table));
              const sheetId = engine.getSheetId(table.key);
              assert(isDefined(sheetId));
              const oldPosition = table.columns.findIndex(
                ({ id }) => id === columnId,
              );
              const newPosition = table.columns.filter((c) => c.pinned).length;

              // Update store state
              set((state) => {
                const table = state.tables.find(({ id }) => id === tableId);
                assert(isDefined(table));

                // Reorder all columns
                const [column] = table.columns.splice(oldPosition, 1);
                table.columns.splice(newPosition, 0, column);
                table.columns.forEach((column, idx) => (column.order = idx));

                // Reorder cells in each row
                table.rows.forEach((row) => {
                  const [cell] = row.cells.splice(oldPosition, 1);
                  row.cells.splice(newPosition, 0, cell);
                });

                // Mark column as pinned
                column.pinned = true;
              });

              // Notify engine to move column
              if (newPosition < oldPosition) {
                engine.moveColumns(sheetId, oldPosition, 1, newPosition);
              } else if (newPosition > oldPosition) {
                engine.moveColumns(sheetId, oldPosition, 1, newPosition + 1);
              }

              // Apply side-effects
              sideEffects.init();
              sideEffects.applyOnStoreState();
            },

            onDequeue: async () => {
              // Persist changes to server
              const table = get().tables.find((table) => table.id === tableId);
              assert(isDefined(table));
              await DjangoClient.pinColumn({
                tableKind: table.kind,
                columnId,
                sideEffects: sideEffects.serialize(),
              });
              return columnId;
            },
          });
        },

        unpinColumn: (options) => {
          const { tableId, columnId } = options;
          const sideEffects = new SideEffects(engine, get, set);

          return enqueueAction({
            details: {
              name: "unpinColumn",
              args: options,
            },

            onEnqueue: () => {
              // Calculate column's new position
              const table = get().tables.find(({ id }) => id === tableId);
              assert(isDefined(table));
              const sheetId = engine.getSheetId(table.key);
              assert(isDefined(sheetId));
              const oldPosition = table.columns.findIndex(
                ({ id }) => id === columnId,
              );
              const newPosition =
                table.columns.filter((c) => c.pinned).length - 1;

              // Update store state
              set((state) => {
                const table = state.tables.find(({ id }) => id === tableId);
                assert(isDefined(table));

                // Reorder all columns
                const [column] = table.columns.splice(oldPosition, 1);
                table.columns.splice(newPosition, 0, column);
                table.columns.forEach((column, idx) => (column.order = idx));

                // Reorder cells in each row
                table.rows.forEach((row) => {
                  const [cell] = row.cells.splice(oldPosition, 1);
                  row.cells.splice(newPosition, 0, cell);
                });

                // Mark column as unpinned
                column.pinned = false;
              });

              // Notify engine to move column
              if (newPosition < oldPosition) {
                engine.moveColumns(sheetId, oldPosition, 1, newPosition);
              } else if (newPosition > oldPosition) {
                engine.moveColumns(sheetId, oldPosition, 1, newPosition + 1);
              }

              // Apply side-effects
              sideEffects.init();
              sideEffects.applyOnStoreState();
            },

            onDequeue: async () => {
              // Persist changes to server
              const table = get().tables.find((table) => table.id === tableId);
              assert(isDefined(table));
              await DjangoClient.unpinColumn({
                tableKind: table.kind,
                columnId,
                sideEffects: sideEffects.serialize(),
              });
              return columnId;
            },
          });
        },

        uploadFiles: (options) => {
          const { blobs, address } = options;
          return enqueueAction({
            details: {
              name: "uploadFiles",
              args: options,
            },

            // We can't update state optimistically for this action
            onEnqueue: () => undefined,

            onDequeue: async () => {
              // Create file objects
              const newFiles = await Promise.all(
                blobs.map(async (blob) => {
                  const { fileType, fileFormat } = await identifyFile(blob);
                  const processingStatus =
                    fileType === FILE_TYPES_BY_KEY["unknown"].id
                      ? // File type is unknown meaning it's not processable so we tell the backend to skip processing
                        ProcessingStatus["SKIPPED"]
                      : ProcessingStatus["PENDING"];

                  return DjangoClient.createFile({
                    name: blob.name,
                    projectId: project.id,
                    size: blob.size,
                    tenantId,
                    fileType,
                    fileFormat,
                    processingStatus,
                  });
                }),
              );

              // Queue uploads
              newFiles.forEach((newFile, idx) => {
                enqueueFile({
                  drsFile: {
                    id: newFile.id,
                    partSize: newFile.part_size,
                  },
                  project: {
                    id: project.id,
                    name: project.name,
                    key: projectKey,
                    tenantId,
                  },
                  blob: blobs[idx],
                  tenantId,
                });
              });

              // Add files to cell formula
              const fileReferenceIds = newFiles.map(
                ({ reference_id }) => reference_id,
              );
              const hfAddress = convertIdeasCellAddress(address);
              const sideEffects = new SideEffects(engine, get, set);
              const newFormula = sideEffects.addFiles(
                fileReferenceIds,
                hfAddress,
              );
              void get().setCellFormula({ address, formula: newFormula ?? "" });

              return fileReferenceIds;
            },
          });
        },

        /**
         * Executes a specified set of analysis table rows.
         * @param options
         * @returns The IDs of the created tasks.
         */
        executeAnalysisTableRows: (options) => {
          const { tableId, rowIds } = options;
          return enqueueAction({
            details: {
              name: "executeAnalysisTableRows",
              args: options,
            },

            // We can't update state optimistically for this action
            onEnqueue: () => undefined,

            onDequeue: async () => {
              // Parse cell values
              const table = get().tables.find(({ id }) => id === tableId);
              assert(isDefined(table));
              const rows = table.rows.filter(({ id }) => rowIds.includes(id));

              // Convert cell values in into analysis parameter values usable
              // by the Task Execution Service.
              const inputs = rows.map((row) => {
                const paramValues: {
                  column: string;
                  value: AnalysisParamValue;
                }[] = [];

                row.cells.forEach((cell, colIdx) => {
                  const column = table.columns[colIdx];

                  convertCellValueToAnalysisParamValues(cell.value).forEach(
                    (value) => {
                      paramValues.push({
                        column: column.id,
                        value: value,
                      });
                    },
                  );
                });

                // Calculate side-effects
                const sideEffects = new SideEffects(engine, get, set);
                sideEffects.init();
                const updatedCells = sideEffects.convertToStaticFormulas(
                  tableId,
                  row.id,
                );
                updatedCells.forEach((cell) => {
                  engine.setCellContents(cell.address, cell.newFormula);
                });
                sideEffects.applyOnStoreState();

                return {
                  rowId: row.id,
                  paramValues,
                  sideEffects: sideEffects.serialize(),
                };
              });

              // Execute analyses
              const data = await DjangoClient.executeAnalysisTableRows(inputs);

              // Update store state
              set((state) => {
                const table = state.tables.find(({ id }) => id === tableId);
                assert(isDefined(table));
                data.forEach(({ id: rowId, task }) => {
                  const row = table.rows.find((row) => row.id === rowId);
                  assert(isDefined(row));
                  row.task = task;
                  row.editable = false;
                  row.cells.forEach((cell) => {
                    cell.isCustomFormula = true;
                  });
                });
              });

              return data.map(({ task }) => task.id);
            },
          });
        },

        /**
         * Cancels the task associated with a specified analysis table row.
         * @param options
         * @returns The ID of the canceled task.
         */
        cancelTask: (options) => {
          const { tableId, rowId } = options;
          let taskId: string;

          return enqueueAction({
            details: {
              name: "cancelTask",
              args: options,
            },

            onEnqueue: () => {
              // Update store store
              set((state) => {
                const table = state.tables.find(({ id }) => id === tableId);
                assert(isDefined(table));
                const row = table.rows.find(({ id }) => id === rowId);
                assert(isDefined(row));

                // Check that row has a task
                if (row.task === null) {
                  throw new Error("Row is unexecuted");
                }

                // Update task status
                taskId = row.task.id;
                row.task.status = TaskStatus["CANCELED"];
              });
            },

            onDequeue: async () => {
              // Cancel task on server
              await DjangoClient.cancelTask({ taskId });
              return taskId;
            },
          });
        },

        /**
         * Refetches all pending analysis table rows.
         *
         * Updates their task statuses and analysis result values as they
         * complete.
         */
        refetchAnalysisTableRows: async () => {
          // Get pending analysis table rows
          const pendingRowIds = get().tables.flatMap((table) => {
            return table.rows
              .filter(({ task }) => task && !isTerminalTaskStatus(task.status))
              .map((row) => row.id);
          });

          // Fetch task information and analysis result formulas
          const data = await DjangoClient.getAnalysisTableRowByIds({
            rowIds: pendingRowIds,
          });

          set((state) => {
            // Group results by table so that we can more efficiently update its rows
            chain(data.results)
              .groupBy((r) => r.table)
              .forEach((updatedRows, tableId) => {
                // Find the table/sheet that corresponds to what the API returned
                const table = state.tables.find(({ id }) => id === tableId);
                assert(isDefined(table));
                const sheetId = engine.getSheetId(table.key);
                assert(isDefined(sheetId));

                // For each row in our existing table
                table.rows.forEach((currentRow, rowIdx) => {
                  // Is this a row we are polling on?
                  if (pendingRowIds.includes(currentRow.id)) {
                    // Find row from response
                    const responseRow = updatedRows.find(
                      (row) => row.id === currentRow.id,
                    );

                    // If row is not in the response, do nothing
                    if (!responseRow) {
                      return;
                    }

                    currentRow.task = responseRow.task;
                    currentRow.cells.forEach((cell, colIdx) => {
                      // API returns formulas in cell-order
                      const newFormula = responseRow.formulas[colIdx];
                      if (newFormula !== null && cell.formula !== newFormula) {
                        cell.formula = newFormula;
                        engine.setCellContents(
                          { sheet: sheetId, col: colIdx, row: rowIdx },
                          newFormula,
                        );
                      }
                    });
                  }
                });
              })
              .value();
          });
        },

        downloadTableAsCsv: ({ tableId }) => {
          /**
           * Sanitizes a string for CSV export.
           * @param str
           * @returns The sanitized string.
           */
          const sanitizeString = (str: string) => {
            // Escape double quote characters (") by replacing with two double
            // quotes ("")
            let sanitizedStr = str.replace(/"/g, '""');
            // Escape comma characters (,) by wrapping string in double quotes
            sanitizedStr = `"${sanitizedStr}"`;
            return sanitizedStr;
          };

          // Parse table ID
          const table = get().tables.find(({ id }) => id === tableId);
          assert(isDefined(table));
          const sheetId = engine.getSheetId(table.key);
          assert(isDefined(sheetId));

          // Format column group headers
          const groupHeaderRow = table.columns.map((column) => {
            const groupId = column.group;
            const group = table.columnGroups.find(({ id }) => id === groupId);
            return sanitizeString(group?.name ?? "");
          });

          // Format column headers
          const headerRow = table.columns.map((column) => {
            return sanitizeString(column.name);
          });

          // Format cell values
          const contentRows = table.rows.map((row) => {
            return row.cells.map(({ value }) => {
              const valueStr = formatCellValue(value);
              return sanitizeString(valueStr);
            });
          });

          // Combine header and content rows
          const allRows = [
            // Include column group headers only if they exist
            ...(table.columnGroups.length > 0 ? [groupHeaderRow] : []),
            headerRow,
            ...contentRows,
          ];

          // Format row data as a CSV string
          const csv = allRows.map((row) => row.join(",")).join("\n");

          // Download CSV file
          const blob = new Blob(["\ufeff", csv]);
          const url = URL.createObjectURL(blob);
          const filename = `${table.name}.csv`;
          downloadFile(url, filename);
        },

        createAnalysisTableFromDataTable: async ({
          dataTableId,
          analysisTableName,
          analysisTableKey,
          toolVersionId,
          autoPopulateRows,
          paramMap,
          columnGroupName,
          paramKeys,
        }) => {
          const dataTable = get().tables.find(({ id }) => id === dataTableId);
          assert(isDefined(dataTable));
          const dataTableSheetId = engine.getSheetId(dataTable.key);
          assert(isDefined(dataTableSheetId));

          await DjangoClient.createAnalysisTableFromDataTable({
            dataTableId,
            analysisTableName,
            analysisTableKey,
            toolVersionId,
            autoPopulateRows,
            paramMap,
            columnGroupName,
            paramKeys,
            sideEffects: new SideEffects(engine, get, set).serialize(),
            folderId: dataTable.folder,
          });

          const data = await DjangoClient.getTableData({
            projectId: project.id,
          });

          const newAnalysisTable = data.find(
            ({ key }) => key === analysisTableKey,
          );

          assert(isDefined(newAnalysisTable));

          const formattedTable = {
            ...newAnalysisTable,
            toolVersion: newAnalysisTable.tool_version,
            columnGroups: newAnalysisTable.column_groups,
            columns: newAnalysisTable.columns,
            rows: newAnalysisTable.rows.map((row) => ({
              id: row.id,
              index: row.index,
              editable: row.editable,
              joinable: row.joinable,
              task: row.task ?? null,
              toolVersion: row.tool_version ?? null,
              resourceTierId: row.resource_tier ?? null,
              cells: row.cells.map((cell) => ({
                isCustomFormula: cell.is_custom_formula,
                formula: cell.formula ?? "",
                value: null,
              })),
            })),
          };

          // Update store state
          set((state) => {
            state.tables.push(formattedTable);
          });

          // Register new table, columns and rows with engine
          engine.addSheet(formattedTable.key);
          const analysisTableSheetId = engine.getSheetId(formattedTable.key);
          assert(isDefined(analysisTableSheetId));
          engine.addRows(analysisTableSheetId, [0, formattedTable.rows.length]);
          engine.addColumns(analysisTableSheetId, [
            0,
            formattedTable.columns.length,
          ]);
          engine.setSheetContent(
            analysisTableSheetId,
            formattedTable.rows.map((row) =>
              row.cells.map((cell) => cell.formula),
            ),
          );

          const columnGroups = data.find((table) => table.id === dataTableId)
            ?.column_groups;

          const newColumns = data
            .find((table) => table.id === dataTableId)
            ?.columns.slice(dataTable.columns.length);

          // Update store state
          set((state) => {
            const table = state.tables.find(({ id }) => id === dataTableId);
            assert(isDefined(table));
            table.columnGroups = columnGroups ?? [];
            table.columns.push(...(newColumns ?? []));
            table.rows.forEach((row) => {
              newColumns?.forEach((column) => {
                row.cells.push({
                  isCustomFormula: false,
                  formula: column.default_formula ?? "",
                  value: null,
                });
              });
            });
          });

          // Notify engine of new columns
          newColumns?.forEach((column, idx) => {
            const colIndex = idx + dataTable.columns.length;
            engine.addColumns(dataTableSheetId, [colIndex, 1]);
            engine.setCellContents(
              { sheet: dataTableSheetId, col: colIndex, row: 0 },
              new Array(dataTable.rows.length).fill([
                column.default_formula ?? "",
              ]),
            );
          });
          return newAnalysisTable.id;
        },

        /**
         * Converts a formula string into an abstract syntax tree.
         * @param options
         * @returns The abstract syntax tree.
         */
        formulaToAst: ({ tableId, formula }) => {
          const table = get().tables.find(({ id }) => id === tableId);
          assert(isDefined(table));
          const sheetId = engine.getSheetId(table.key);
          assert(isDefined(sheetId));
          return engine.formulaToAst(formula, sheetId).ast;
        },

        /**
         * Maps columns from data tables and analysis tables to parameters in
         * a specified analysis table.
         * @param options
         */
        mapAnalysisParameters: async (options) => {
          const { tableId, sourceTableIds, paramMap } = options;
          const sideEffects = new SideEffects(engine, get, set);

          return enqueueAction({
            details: {
              name: "mapAnalysisParameters",
              args: options,
            },

            // We can't update state optimistically for this action
            onEnqueue: () => undefined,

            onDequeue: async () => {
              sideEffects.updateAnalysisParameterSourceColumns(
                tableId,
                sourceTableIds,
              );

              // Persist parameter mappings to server
              await DjangoClient.mapAnalysisParameters({
                analysisTableId: tableId,
                sourceTables: sourceTableIds.map((tableId) => {
                  const table = get().tables.find(({ id }) => id === tableId);
                  assert(isDefined(table));
                  return {
                    id: tableId,
                    kind:
                      table.kind === "data"
                        ? "data_table"
                        : "gdt_analysis_table",
                  };
                }),
                paramMap,
                sideEffects: sideEffects.serialize(),
              });

              // Refetch column default formulas
              const data = await DjangoClient.getTableData({
                projectId: project.id,
              });
              const analysisTableData = data.find(({ id }) => id === tableId);
              assert(isDefined(analysisTableData));

              // Update store state
              set((state) => {
                const table = state.tables.find(({ id }) => id === tableId);
                assert(isDefined(table));

                // Update column
                table.columns = analysisTableData.columns;

                // Update row formulas
                table.rows = analysisTableData.rows.map((row) => ({
                  id: row.id,
                  index: row.index,
                  editable: row.editable,
                  joinable: row.joinable,
                  toolVersion: row.tool_version ?? null,
                  resourceTierId: row.resource_tier ?? null,
                  task: row.task ?? null,
                  cells: row.cells.map((cell) => ({
                    isCustomFormula: cell.is_custom_formula,
                    formula: cell.formula ?? "",
                    value: null,
                  })),
                }));
              });

              // Calculate sheet ID
              const table = get().tables.find(({ id }) => id === tableId);
              assert(isDefined(table));
              const sheetId = engine.getSheetId(table.key);
              assert(isDefined(sheetId));

              // Notify engine of new formulas
              engine.setCellContents(
                { sheet: sheetId, col: 0, row: 0 },
                table.rows.map((row) => row.cells.map((cell) => cell.formula)),
              );

              // Apply side-effects to store state
              sideEffects.applyOnStoreState();

              // Apply side-effects on engine
              sideEffects.applyOnEngineState();
            },
          });
        },

        /**
         *
         * @param options.address Address of the top-left cell where the
         * clipboard data should be pasted.
         * @param options.clipboardData Formulas that should be pasted if the
         * clipboard data comes from somewhere external to IDEAS.
         * @returns
         */
        pasteCellFormula: (options) => {
          const { address, clipboardData } = options;
          const sideEffects = new SideEffects(engine, get, set);

          return enqueueAction({
            details: {
              name: "pasteCellFormula",
              args: options,
            },

            onEnqueue: () => {
              // Convert cell address to HyperFormula cell address
              const hfAddress = convertIdeasCellAddress(address);

              const updatedFormulas: {
                address: SimpleCellAddress;
                newFormula: string;
              }[] = [];

              set((state) => {
                // Mark cells as having custom formulas
                const table = state.tables.find(
                  (t) => t.id === address.tableId,
                );
                assert(isDefined(table));
                for (let i = 0; i < clipboardData.length; i++) {
                  for (let j = 0; j < clipboardData[i].length; j++) {
                    const newFormula = clipboardData[i][j];
                    const rowIdx = hfAddress.row + i;
                    const colIdx = hfAddress.col + j;
                    if (
                      rowIdx < table.rows.length &&
                      colIdx < table.columns.length
                    ) {
                      const row = table.rows[rowIdx];
                      const column = table.columns[colIdx];
                      const cell = row.cells[colIdx];
                      if (row.editable && column.editable) {
                        cell.isCustomFormula = true;
                        updatedFormulas.push({
                          address: {
                            sheet: hfAddress.sheet,
                            col: colIdx,
                            row: rowIdx,
                          },
                          newFormula,
                        });
                      }
                    }
                  }
                }
              });

              // Update formulas in engine
              engine.batch(() => {
                updatedFormulas.forEach(({ address, newFormula }) => {
                  engine.setCellContents(address, newFormula);
                });
              });

              // Calculate side-effects
              sideEffects.init();
              sideEffects.applyOnStoreState();
            },

            onDequeue: async () => {
              // Persist new cell formulas to server
              await DjangoClient.applySideEffects({
                projectId: project.id,
                sideEffects: sideEffects.serialize(),
              });
            },
          });
        },

        copyAnalysisTableRow: (options) => {
          const { tableId, rowId } = options;
          const sideEffects = new SideEffects(engine, get, set);

          return enqueueAction({
            details: {
              name: "copyAnalysisTableRow",
              args: options,
            },

            // We can't update state optimistically for this action
            onEnqueue: () => undefined,

            onDequeue: async () => {
              // Parse table ID
              const table = get().tables.find(({ id }) => id === tableId);
              assert(isDefined(table));
              const sheetId = engine.getSheetId(table.key);
              assert(isDefined(sheetId));
              const sourceRow = table.rows.find(({ id }) => id === rowId);
              assert(isDefined(sourceRow));

              // Apply side-effects
              sideEffects.init();
              sideEffects.applyOnStoreState();

              // Create new row on server
              // Apply resource tier
              const newRow = await DjangoClient.createRow({
                projectId: project.id,
                tableId,
                tableKind: table.kind,
                toolVersionId: sourceRow.toolVersion?.id ?? null,
                resourceTierId: sourceRow.resourceTierId,
                sideEffects: sideEffects.serialize(),
              });

              // Calculate the new row's cell formulas and values
              const newRowCells: {
                isCustomFormula: boolean;
                formula: string;
                value: CellValue;
              }[] = (() => {
                // Copy cells directly if source row is unexecuted
                if (!sourceRow.task) {
                  return clone(sourceRow.cells);
                }

                return table.columns.map((column, idx) => {
                  // Do not copy cells in analysis result columns
                  if (column.is_result) {
                    return {
                      isCustomFormula: false,
                      formula: "",
                      value: null,
                    };
                  }

                  // Copy cells in parameter source columns
                  const columnGroup = table.columnGroups.find(
                    ({ id }) => id === column.group,
                  );
                  if (columnGroup?.definition?.kind === "sources") {
                    return {
                      isCustomFormula: true,
                      formula: sourceRow.cells[idx].formula,
                      value: sourceRow.cells[idx].value,
                    };
                  }

                  // Copy parameters with custom formulas
                  const cell = sourceRow.cells[idx];
                  const shouldUseDefault = engine.validateFormula(
                    column.default_formula ?? "",
                  );
                  if (
                    shouldUseDefault ||
                    column.default_formula === cell.formula
                  ) {
                    return {
                      isCustomFormula: false,
                      formula: column.default_formula ?? "",
                      value: cell.value,
                    };
                  } else {
                    return {
                      isCustomFormula: true,
                      formula: cell.formula,
                      value: cell.value,
                    };
                  }
                });
              })();

              // Add new row to store state
              set((state) => {
                const table = state.tables.find(({ id }) => id === tableId);
                assert(isDefined(table));
                table.rows.push({
                  ...newRow,
                  task: null,
                  toolVersion: sourceRow.toolVersion,
                  resourceTierId: sourceRow.resourceTierId,
                  cells: newRowCells,
                });
              });

              // Register new row with engine
              const newRowIndex = table.rows.length;
              engine.addRows(sheetId, [newRowIndex, 1]);
              engine.setCellContents(
                { sheet: sheetId, col: 0, row: newRowIndex },
                [newRowCells.map((cell) => cell.formula)],
              );

              // Update custom cell formulas. TODO: Do this in a single traction
              // when creating the row.
              await DjangoClient.applySideEffects({
                projectId: project.id,
                sideEffects: {
                  analysis_table_cells: newRowCells
                    .map((cell, idx) => {
                      if (cell.isCustomFormula) {
                        return {
                          project: project.id,
                          table: tableId,
                          column: table.columns[idx].id,
                          row: newRow.id,
                          formula: cell.formula,
                        };
                      } else {
                        return undefined;
                      }
                    })
                    .filter(isDefined),
                  analysis_table_columns: [],
                  data_table_cells: [],
                  data_table_columns: [],
                },
              });

              return newRow.id;
            },
          });
        },

        /**
         * Gets the name of the sheet registered with the HyperFormula engine
         * with a specified ID. In our system, the sheet name is equivalent to
         * the table key.
         * @param options.sheetId
         * @returns The name of the sheet or `undefined` if no sheet is
         * registered with the engine with the specified ID.
         */
        getSheetName: ({ sheetId }) => {
          return engine.getSheetName(sheetId);
        },

        /**
         * Creates a lookup map of all files and the cells where they are
         * directly referenced inside formulas.
         * @param filters.addressRanges Optional filter to remove files not
         * referenced in at least one of the specified address ranges.
         * @param filters.maxRefCount Optional filter to remove files referenced
         * more than a certain number of times.
         * @returns A lookup map from file ID to cell addresses.
         */
        getFileAssignments: ({
          addressRanges = [],
          maxRefCount = Infinity,
        }) => {
          /**
           * Gets all file reference IDs from a formula AST.
           * @param ast
           * @returns An array of file reference IDs.
           */
          const getFileReferenceIds = (ast: Ast) => {
            const referenceIds = new Set<string>();

            traverseAst(ast, (node) => {
              if (
                node.type === AstNodeType.FUNCTION_CALL &&
                node.procedureName === IdeasFunctionPluginTranslations.enGB.FILE
              ) {
                assert(node.args[0].type === AstNodeType.STRING);
                const referenceId = node.args[0].value;
                referenceIds.add(referenceId);
              }
            });

            return Array.from(referenceIds);
          };

          /**
           * Creates a lookup map of all files and the cells where they are
           * directly referenced inside formulas.
           * @returns A lookup map from file ID to cell addresses.
           */
          const getAllFileAssignments = () => {
            const map: Record<string, CellAddress[]> = {};
            const sheets = engine.getAllSheetsFormulas();

            Object.entries(sheets).forEach(([sheetName, sheet]) => {
              // Parse sheet ID
              const sheetId = engine.getSheetId(sheetName);
              assert(isDefined(sheetId));
              const table = get().tables.find((t) => t.key === sheetName);
              assert(isDefined(table));

              sheet.forEach((row, rowIdx) => {
                row.forEach((formula, colIdx) => {
                  if (formula !== undefined) {
                    const { ast } = engine.formulaToAst(formula, sheetId);
                    getFileReferenceIds(ast).forEach((referenceId) => {
                      const file =
                        get().fileMap.filesByReferenceId[referenceId];

                      if (file === undefined || file === IdeasFileNotFound) {
                        return;
                      } else if (!(file.id in map)) {
                        map[file.id] = [];
                      }

                      map[file.id].push({
                        tableId: table.id,
                        columnId: table.columns[colIdx].id,
                        rowId: table.rows[rowIdx].id,
                      });
                    });
                  }
                });
              });
            });

            return map;
          };

          /**
           * Determines whether a given cell address falls within a specified
           * address range.
           *
           * The function checks if the provided `address` matches the
           * constraints defined in the `range`. If `tableId`, `columnId`, or
           * `rowId` are defined in the range, the corresponding property in the
           * address must match. If any defined property does not match, the
           * function returns `false`.
           *
           * @param address - The cell address to check.
           * @param range - The address range to check against. May specify
           * `tableId`, `columnId`, and/or `rowId`.
           * @returns `true` if the address is within the specified range;
           * otherwise, `false`.
           */
          const inCellRange = (
            address: CellAddress,
            range: (typeof addressRanges)[number],
          ) => {
            if (isDefined(range.tableId) && address.tableId !== range.tableId) {
              return false;
            }

            if (
              isDefined(range.columnId) &&
              address.columnId !== range.columnId
            ) {
              return false;
            }

            if (isDefined(range.rowId) && address.rowId !== range.rowId) {
              return false;
            }

            return true;
          };

          const assignments = getAllFileAssignments();

          // Filters out assignments that do not fall in the specified address
          // ranges or are referenced more times than the allowed maximum
          Object.entries(assignments).forEach(([fileId, addresses]) => {
            // Filter out files referenced more than the specified maximum
            const tableIds = new Set(addresses.map((a) => a.tableId));
            if (tableIds.size > maxRefCount) {
              delete assignments[fileId];
              return;
            }

            // Filter out files not assigned to at least one cell in the
            // specified address ranges
            const inAtLeastOneAddressRange =
              addressRanges.length > 0 &&
              addresses.some((address) => {
                return addressRanges.some((range) => {
                  return inCellRange(address, range);
                });
              });

            if (!inAtLeastOneAddressRange) {
              delete assignments[fileId];
              return;
            }
          });

          return assignments;
        },
      } satisfies DataTableStoreState;
    }),
  );
};
